import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FocalImage } from "@/components/ui/focal-image";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSharedRecipe } from "@/lib/recipe-sharing-service";
import { IngredientHighlighter } from "@/lib/ingredient-highlighter";
import type { Recipe } from "@/lib/types";
import { Share2, Lock, Copy, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { ShareButtons } from "@/components/ShareButtons";
import { Helmet } from "react-helmet-async";

const FALLBACK_RECIPE_IMAGE = "https://picsum.photos/seed/recipe-detail/960/540";

export default function PublicRecipePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [lockedPreview, setLockedPreview] = useState<{ title: string; imageUrl: string; imageHint: string } | null>(
    null,
  );
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing recipe token");
      setLoading(false);
      return;
    }

    async function loadRecipe() {
      try {
        setLoading(true);
        setError(null);
        const result = await getSharedRecipe(token!);

        if (!result.recipe && !result.passwordRequired) {
          setError("Recipe not found or is no longer publicly shared.");
          setLoading(false);
          return;
        }

        if (result.passwordRequired) {
          setPasswordRequired(true);
          setLockedPreview(result.lockedPreview ?? null);
          setRecipe(null);
        } else {
          setRecipe(result.recipe);
          setIsUnlocked(true);
          setPasswordRequired(false);
          setLockedPreview(null);
        }

        // Set share URL
        const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
        setShareUrl(`${baseUrl}/share/recipe/${token}`);
      } catch (err) {
        console.error("Error loading recipe:", err);
        setError("Failed to load recipe. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    loadRecipe();
  }, [token]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setPasswordError(null);

    try {
      const result = await getSharedRecipe(token!, password);

      if (result.recipe) {
        setRecipe(result.recipe);
        setIsUnlocked(true);
        setPasswordRequired(false);
        setLockedPreview(null);
      } else {
        setPasswordError("Incorrect password. Please try again.");
      }
    } catch (err) {
      console.error("Error verifying password:", err);
      setPasswordError("Failed to verify password. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // Generate meta tags for SEO
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://ezrecipebox.com";
  const recipeTitle = recipe?.title || lockedPreview?.title || "Recipe";
  const recipeDescription = recipe?.notes?.substring(0, 160) || `View this recipe: ${recipeTitle}`;
  const recipeImage = recipe?.imageUrl || lockedPreview?.imageUrl || `${siteUrl}/assets/logo.png`;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading Recipe... | ezRecipeBox</title>
        </Helmet>
        <div className="mx-auto max-w-4xl space-y-8 p-4 sm:p-6 lg:p-8">
          <div className="rounded-lg border border-dashed p-6 text-center">
            <h2 className="font-headline text-xl">Loading recipe...</h2>
          </div>
        </div>
      </>
    );
  }

  if (error || (!recipe && !passwordRequired)) {
    return (
      <>
        <Helmet>
          <title>Recipe Not Found | ezRecipeBox</title>
        </Helmet>
        <div className="mx-auto max-w-4xl space-y-8 p-4 sm:p-6 lg:p-8">
          <Alert variant="destructive">
            <AlertTitle>Unable to load recipe</AlertTitle>
            <AlertDescription>{error || "Recipe not found"}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => navigate("/")}>
            Go to Home
          </Button>
        </div>
      </>
    );
  }

  // Password protection screen
  if (passwordRequired && !isUnlocked) {
    return (
      <>
        <Helmet>
          <title>{lockedPreview?.title || "Password Protected Recipe"} | ezRecipeBox</title>
          <meta name="description" content="This recipe is password protected." />
        </Helmet>
        <div className="mx-auto max-w-md space-y-8 p-4 sm:p-6 lg:p-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                <CardTitle>{lockedPreview?.title ?? "Password Protected Recipe"}</CardTitle>
              </div>
              <CardDescription>
                This recipe is password protected. Please enter the password to view it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    disabled={isVerifying}
                  />
                  {passwordError && (
                    <p className="text-sm text-destructive">{passwordError}</p>
                  )}
                </div>
                <Button type="submit" disabled={isVerifying} className="w-full">
                  {isVerifying ? "Verifying..." : "Unlock Recipe"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // Recipe display
  const recipeImageUrl = recipe?.imageUrl || FALLBACK_RECIPE_IMAGE;

  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const directions = recipe?.directions || "";

  const handleCopy = async () => {
    await handleCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>{recipeTitle} | ezRecipeBox</title>
        <meta name="description" content={recipeDescription} />
        <meta property="og:title" content={recipeTitle} />
        <meta property="og:description" content={recipeDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:image" content={recipeImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={recipeTitle} />
        <meta name="twitter:description" content={recipeDescription} />
        <meta name="twitter:image" content={recipeImage} />
      </Helmet>
      <div className="mx-auto max-w-4xl space-y-8 p-4 sm:p-6 lg:p-8">
        {/* Share Header */}
        <div className="flex items-center justify-between rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Shared Recipe</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopy}
              className="gap-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Link
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Recipe Title */}
        <div>
          <h1 className="font-headline text-4xl font-bold">{recipe?.title}</h1>
          {recipe?.labels && recipe.labels.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {recipe.labels.map((label, index) => (
                <span
                  key={index}
                  className="rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Recipe Image */}
        {recipeImageUrl && (
          <div className="relative aspect-video w-full overflow-hidden rounded-lg">
            <FocalImage
              src={recipeImageUrl}
              alt={recipe?.imageHint || recipe?.title || "Recipe image"}
              className="h-full w-full"
              focalPointX={recipe?.imageFocalPointX}
              focalPointY={recipe?.imageFocalPointY}
              zoom={recipe?.imageZoom}
            />
          </div>
        )}

        {/* Recipe Meta Info */}
        <div className="grid gap-4 md:grid-cols-3">
          {recipe?.prepTime && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Prep Time</div>
              <div className="mt-1 font-semibold">{recipe.prepTime}</div>
            </div>
          )}
          {recipe?.cookTime && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Cook Time</div>
              <div className="mt-1 font-semibold">{recipe.cookTime}</div>
            </div>
          )}
          {recipe?.servings && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm text-muted-foreground">Serves</div>
              <div className="mt-1 font-semibold">{recipe.servings}</div>
            </div>
          )}
        </div>

        {/* Ingredients */}
        {ingredients.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Ingredients</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {ingredients.map((ingredient, index) => {
                  const isHeader = ingredient.trim().startsWith("##");
                  if (isHeader) {
                    return (
                      <li key={index} className="list-none pt-3 pb-1 font-semibold text-foreground">
                        {ingredient.trim().replace(/^##\s*/, "")}
                      </li>
                    );
                  }
                  return (
                    <li key={index} className="flex items-start gap-2">
                      <span className="mt-2 block h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                      <span>{IngredientHighlighter.renderHighlightedIngredient(ingredient)}</span>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Directions */}
        {directions && (
          <Card>
            <CardHeader>
              <CardTitle>Directions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-foreground">
                {directions
                  .split(/\r?\n/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .map((step, index) => {
                    const isHeader = step.startsWith("##");
                    const key = step.toLowerCase() || `step-${index}`;
                    if (isHeader) {
                      return (
                        <p key={key} className="pt-3 pb-1 font-semibold text-foreground">
                          {step.replace(/^##\s*/, "")}
                        </p>
                      );
                    }
                    return (
                      <p key={key} className="leading-relaxed">
                        {step}
                      </p>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {recipe?.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-muted-foreground">{recipe.notes}</div>
            </CardContent>
          </Card>
        )}

        {/* Share Buttons */}
        {recipe && <ShareButtons recipe={recipe} shareUrl={shareUrl} />}

        {/* Call to Action */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="font-headline text-xl font-semibold">Love this recipe?</h3>
              <p className="mt-2 text-muted-foreground">
                Create your own recipe collection with ezRecipeBox
              </p>
              <Button className="mt-4" onClick={() => navigate("/signup")}>
                Get Started Free
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

